import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { KeyRound, Loader2, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Set a new password from an emailed reset link (?reset=TOKEN). */
export const ResetPasswordForm = ({ token, endpoint, onDone }) => {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === 'string' ? d.detail : 'Could not reset password.'); return; }
      setDone(true);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003D5C] to-[#0077B6] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="h-8 w-8 text-[#003D5C]" />
          </div>
          <CardTitle className="text-2xl">Choose a New Password</CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700" data-testid="reset-success">
                Your password has been updated. You can now sign in.
              </div>
              <Button onClick={onDone} className="w-full bg-[#0077B6] hover:bg-[#005A8C]" data-testid="reset-back-to-login">Back to Login</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="reset-error">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]" data-testid="reset-new-password" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]" data-testid="reset-confirm-password" />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#0077B6] hover:bg-[#005A8C]" data-testid="reset-submit">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Set New Password'}
              </Button>
              <button type="button" onClick={onDone} className="w-full text-sm text-gray-600 hover:text-[#0077B6]">Back to Login</button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordForm;
